import { useQueryClient } from 'react-query'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom-v5-compat'
import { NAV_LEFT_WRAP } from 'shared/components/Nav/NavLeft'
import { NotificationBell } from 'shared/components/Notifications/NotificationBell'
import { useBreakpoint } from 'shared/hooks/useBreakpoint'
import { Colors } from 'shared/theme/colors'
import styled from 'styled-components'

import { ONBOARDING_V2_REDIRECTED_KEY } from 'helpers/constants'
import { logoutUser } from 'redux/actions/auth/authActions'
import { ReduxDispatch } from 'redux/store/models'
import { useAnalyticsEvent } from '../../../hooks/useAnalyticsEvent'
import { useLogRocket } from '../../../hooks/useLogRocket'
import { usePermission } from '../../../hooks/usePermission'
import { PermissionType } from '../../../models/permission'
import { AskAiButton } from '../../../pages/Consultant/AskAiButton'
import { currentUserSelector, isVendorUserSelector } from '../../../redux/selectors/users'
import { SIGN_IN_PATH } from '../../../Routes'
import { useLayoutContext } from '../../Context/LayoutContext'
import { AppCompanyBrand } from './AppCompanyBrand'
import { AvatarMenu } from './AvatarMenu'
import {
  AttentionSlot,
  brandedNavigation,
  NavigationActions,
  NavigationBrand,
} from './BrandedNavigation.styles'
import { RateconAttentionBanner } from './RateconAttentionBanner'
import { useShowSideBar } from './useShowSideBar'

export const AppNav = () => {
  const navigate = useNavigate()
  const dispatch: ReduxDispatch = useDispatch()
  const queryClient = useQueryClient()
  const track = useAnalyticsEvent()
  const showSidebar = useShowSideBar()
  const isMobile = useBreakpoint('xs')
  const { isSideBarCollapsed } = useLayoutContext()
  const user = useSelector(currentUserSelector)
  const isVendorUser = useSelector(isVendorUserSelector)
  const companyHeaderVisible =
    showSidebar && !isSideBarCollapsed && !isMobile && user.organization && !user.is_staff && !isVendorUser
  const hasAiConsultantPermission = usePermission(PermissionType.AI_CONSULTANT)
  const companyUser = { ...user, organization: user.organization || '' }

  useLogRocket()

  const handleLogout = () => {
    track('profile_logout_click')

    localStorage?.removeItem?.(ONBOARDING_V2_REDIRECTED_KEY)
    dispatch(logoutUser())
    queryClient.clear()
    navigate(SIGN_IN_PATH, { replace: true })
  }

  return (
    <NavWrap className="app-nav" $withMobileMenu={showSidebar && isMobile}>
      {!companyHeaderVisible && (
        <NavigationBrand>
          <AppCompanyBrand />
        </NavigationBrand>
      )}
      <div id={NAV_LEFT_WRAP} />
      <AttentionSlot>
        <RateconAttentionBanner user={companyUser} />
      </AttentionSlot>
      <NavigationActions>
        {hasAiConsultantPermission.enabled && <AskAiButton iconOnly={isMobile} />}
        <NotificationBell user={companyUser} />
        <AvatarMenu handleLogout={handleLogout} />
      </NavigationActions>
    </NavWrap>
  )
}

const NavWrap = styled.div<{ $withMobileMenu: boolean }>`
  min-height: 64px;
  background-color: ${Colors.white};
  ${brandedNavigation}
  position: relative;
  justify-content: space-between;
  padding-left: ${(p) => (p.$withMobileMenu ? '44px' : '0')};
`
